import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Cell, PieChart, Pie, Legend,
} from "recharts";
import {
  Megaphone, Plus, Euro, TrendingUp, MousePointerClick, Target, Percent,
  Wallet, Pencil, Trash2, Play, Pause, Square, Copy, X, Users, BarChart3,
  MapPin, Monitor, Clock, Eye, ShoppingCart, Route, ArrowRight, Globe,
  Archive, RotateCcw, LogIn, Tag, FileText, CheckCircle2, Search, Wifi,
  Smartphone, ShieldCheck, ShieldOff, Ban, Bot, RefreshCw,
} from "lucide-react";
import { fmtTimeExact } from "@/lib/regie";
import api, { eur, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";

const GOLD = "#D4AF37";
const INK = "#1A1918";
const COPPER = "#C08B5C";
const SAGE = "#8A9A7B";

const CHANNEL_LABELS = {
  google_ads: "Google Ads", meta: "Meta / Facebook", instagram: "Instagram",
  tiktok: "TikTok", email: "Email", affiliate: "Affilié / Partenaire",
  influence: "Influence", display: "Display", autre: "Autre",
};

const STATUS_STYLES = {
  active: "bg-green-100 text-green-800 border-green-200",
  paused: "bg-amber-100 text-amber-800 border-amber-200",
  ended: "bg-stone/40 text-graphite border-stone",
};
const STATUS_LABELS = { active: "Active", paused: "En pause", ended: "Terminée" };

const Stat = ({ icon: Icon, label, value, sub, testid }) => (
  <div data-testid={testid} className="bg-white rounded-sm border border-stone p-5">
    <div className="flex items-center justify-between">
      <p className="text-xs uppercase tracking-[0.16em] text-ash">{label}</p>
      <Icon className="h-5 w-5 text-copper" strokeWidth={1.5} />
    </div>
    <p className="font-serif text-3xl text-ink mt-3">{value}</p>
    {sub && <p className="text-xs text-ash mt-1">{sub}</p>}
  </div>
);

const Card = ({ title, children, action }) => (
  <div className="bg-white border border-stone rounded-sm p-5">
    <div className="flex items-center justify-between mb-4">
      <h2 className="font-serif text-xl text-ink">{title}</h2>
      {action}
    </div>
    {children}
  </div>
);

const fmtPct = (v) => (v === null || v === undefined ? "—" : `${v}%`);
const fmtMoney = (v) => (v === null || v === undefined ? "—" : eur(v));

const emptyForm = {
  name: "", channel: "google_ads", partner_name: "", budget: "",
  commission_type: "percent", commission_value: "", landing_path: "/",
  start_date: "", end_date: "", notes: "",
};

const Lbl = ({ children }) => (
  <label className="text-xs uppercase tracking-[0.15em] text-ash">{children}</label>
);
const inputCls = "w-full mt-1.5 border border-stone bg-white px-3 py-2.5 text-sm text-ink focus:outline-none focus:border-gold";

function CampaignForm({ initial, onClose, onSaved }) {
  const editing = !!initial?.id;
  const [form, setForm] = useState(() =>
    initial
      ? {
          name: initial.name || "", channel: initial.channel || "autre",
          partner_name: initial.partner_name || "", budget: initial.budget ?? "",
          commission_type: initial.commission_type || "percent",
          commission_value: initial.commission_value ?? "",
          landing_path: initial.landing_path || "/",
          start_date: initial.start_date || "", end_date: initial.end_date || "",
          notes: initial.notes || "",
        }
      : emptyForm
  );
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Le nom de la campagne est requis.");
    setSaving(true);
    const payload = {
      ...form,
      budget: parseFloat(form.budget) || 0,
      commission_value: parseFloat(form.commission_value) || 0,
    };
    try {
      if (editing) await api.put(`/superadmin/campaigns/${initial.id}`, payload);
      else await api.post("/superadmin/campaigns", payload);
      toast.success(editing ? "Campagne mise à jour." : "Campagne créée.");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start md:items-center justify-center bg-ink/50 p-4 overflow-y-auto">
      <div className="bg-sand w-full max-w-2xl my-8 border border-stone" data-testid="campaign-form">
        <div className="flex items-center justify-between p-5 border-b border-stone bg-white">
          <h2 className="font-serif text-2xl text-ink">{editing ? "Modifier la campagne" : "Nouvelle campagne"}</h2>
          <button onClick={onClose} className="text-ash hover:text-ink"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <Lbl>Nom de la campagne</Lbl>
            <input data-testid="campaign-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Ex: Soldes Hiver Google" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Lbl>Canal</Lbl>
              <select data-testid="campaign-channel" className={inputCls} value={form.channel} onChange={set("channel")}>
                {Object.entries(CHANNEL_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <Lbl>Partenaire (optionnel)</Lbl>
              <input className={inputCls} value={form.partner_name} onChange={set("partner_name")} placeholder="Nom de l'affilié / agence" />
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Lbl>Budget / Dépense (€)</Lbl>
              <input data-testid="campaign-budget" type="number" step="0.01" min="0" className={inputCls} value={form.budget} onChange={set("budget")} placeholder="0.00" />
            </div>
            <div>
              <Lbl>Type commission</Lbl>
              <select data-testid="campaign-commission-type" className={inputCls} value={form.commission_type} onChange={set("commission_type")}>
                <option value="percent">% du CA</option>
                <option value="fixed">Fixe / vente (€)</option>
              </select>
            </div>
            <div>
              <Lbl>Valeur commission</Lbl>
              <input data-testid="campaign-commission-value" type="number" step="0.01" min="0" className={inputCls} value={form.commission_value} onChange={set("commission_value")} placeholder={form.commission_type === "percent" ? "10" : "5.00"} />
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Lbl>Page d&apos;atterrissage</Lbl>
              <input className={inputCls} value={form.landing_path} onChange={set("landing_path")} placeholder="/" />
            </div>
            <div>
              <Lbl>Début (optionnel)</Lbl>
              <input type="date" className={inputCls} value={form.start_date} onChange={set("start_date")} />
            </div>
            <div>
              <Lbl>Fin (optionnel)</Lbl>
              <input type="date" className={inputCls} value={form.end_date} onChange={set("end_date")} />
            </div>
          </div>
          <div>
            <Lbl>Notes</Lbl>
            <textarea className={inputCls} rows={2} value={form.notes} onChange={set("notes")} />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-stone text-graphite py-3.5 uppercase tracking-[0.15em] text-xs hover:bg-white transition-colors">Annuler</button>
            <button data-testid="save-campaign-btn" type="submit" disabled={saving} className="flex-1 bg-ink text-cream py-3.5 uppercase tracking-[0.15em] text-xs hover:bg-gold hover:text-ink transition-colors disabled:opacity-60">
              {saving ? "Enregistrement…" : editing ? "Mettre à jour" : "Créer la campagne"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const fmtDuration = (ms) => {
  if (!ms || ms <= 0) return "—";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  if (m < 60) return `${m}m ${rs}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

const flagEmoji = (cc) => {
  if (!cc || cc.length !== 2) return "";
  try {
    return String.fromCodePoint(...cc.toUpperCase().split("").map((c) => 127397 + c.charCodeAt(0)));
  } catch (e) {
    return "";
  }
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch (e) {
    return iso.slice(0, 16);
  }
};

const MiniBar = ({ label, count, total, icon: Icon }) => (
  <div className="flex items-center gap-3 text-sm">
    {Icon && <Icon className="h-4 w-4 text-ash shrink-0" strokeWidth={1.5} />}
    <span className="text-graphite w-32 truncate shrink-0">{label}</span>
    <div className="flex-1 bg-sand h-2 rounded-full overflow-hidden">
      <div className="h-full bg-gold" style={{ width: `${total ? (count / total) * 100 : 0}%` }} />
    </div>
    <span className="text-ink font-medium w-8 text-right">{count}</span>
  </div>
);

const STEP_CONF = {
  arrival: { icon: LogIn, dot: "bg-ink border-ink", ring: "text-cream", chip: "bg-ink/5 text-ink", title: "Arrivée sur le site" },
  product: { icon: Eye, dot: "bg-gold border-gold", ring: "text-ink", chip: "bg-gold/10 text-ink", title: "Produit consulté" },
  cart: { icon: ShoppingCart, dot: "bg-copper border-copper", ring: "text-cream", chip: "bg-copper/10 text-copper", title: "Ajout au panier" },
  conversion: { icon: CheckCircle2, dot: "bg-green-600 border-green-600", ring: "text-white", chip: "bg-green-100 text-green-800", title: "Commande passée" },
  category: { icon: Tag, dot: "bg-white border-stone", ring: "text-ash", chip: "", title: "Catégorie parcourue" },
  page: { icon: FileText, dot: "bg-white border-stone", ring: "text-ash", chip: "", title: "Page vue" },
  blocked: { icon: ShieldOff, dot: "bg-red-600 border-red-600", ring: "text-white", chip: "bg-red-100 text-red-700", title: "Accès refusé" },
  block_applied: { icon: Ban, dot: "bg-red-700 border-red-700", ring: "text-white", chip: "bg-red-100 text-red-700", title: "Blocage appliqué" },
};

// Badge robot : Google/Bing vérifié (vert), imposteur (rouge), autre robot (gris)
export const BOT_STYLES = {
  verified: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: (n) => `${n || "Robot"} vérifié` },
  impostor: { cls: "bg-red-100 text-red-700 border-red-200", label: (n) => `Faux ${n || "robot"} (IP non vérifiée)` },
  bot: { cls: "bg-stone/50 text-graphite border-stone", label: (n) => `Robot · ${n || "inconnu"}` },
};
export const BotBadge = ({ v, compact = false }) => {
  if (!v?.bot_type) return null;
  const conf = BOT_STYLES[v.bot_type] || BOT_STYLES.bot;
  return (
    <span
      data-testid="bot-badge"
      title={v.bot_type === "verified" ? "Robot authentifié par les plages IP officielles du moteur" : v.bot_type === "impostor" ? "Se prétend Googlebot/Bingbot mais son IP n'appartient pas au moteur : robot malveillant probable" : "Outil automatisé ou robot tiers (User-Agent)"}
      className={`inline-flex items-center gap-1 ${compact ? "px-1.5 py-0.5 text-[9px]" : "px-2 py-0.5 text-[10px]"} uppercase tracking-[0.08em] border rounded-sm whitespace-nowrap ${conf.cls}`}
    >
      {conf.label(v.bot_name)}
    </span>
  );
};

// Score de risque 0–100 (backend) : pastille colorée + raisons au survol
export const RiskPill = ({ risk, compact = false }) => {
  if (!risk || typeof risk.score !== "number" || risk.score <= 0) return null;
  const cls = risk.score >= 70 ? "bg-red-600 text-white border-red-700" : risk.score >= 40 ? "bg-red-100 text-red-700 border-red-200" : risk.score >= 20 ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-stone/40 text-graphite border-stone";
  return (
    <span data-testid="risk-pill" title={`Risque ${risk.level} : ${(risk.reasons || []).join(" · ") || "aucun signal"}`}
      className={`inline-flex items-center gap-1 ${compact ? "px-1.5 py-0.5 text-[9px]" : "px-2 py-0.5 text-[10px]"} uppercase tracking-[0.06em] rounded-sm border whitespace-nowrap ${cls}`}>
      risque {risk.score}
    </span>
  );
};

// Badge « appareil nomade » : même identifiant de session vu sur plusieurs IP / pays.
// Jusqu'à 2 valeurs affichées inline, au-delà « +N » (le détail complet est dans le parcours).
export const MobilityBadge = ({ v, compact = false }) => {
  const ips = v?.ip_history || (v?.linked_ips ? [v.ip, ...v.linked_ips] : []);
  const countries = v?.countries || v?.linked_countries || [];
  const multiIp = ips.length > 1;
  const multiCountry = countries.length > 1;
  if (!multiIp && !multiCountry) return null;
  const shownIps = ips.slice(0, 2);
  const moreIps = ips.length - shownIps.length;
  const shownC = countries.slice(0, 2);
  const moreC = countries.length - shownC.length;
  const title = `Même appareil (identifiant de session conservé) vu sur ${ips.length} IP${ips.length > 1 ? "s" : ""} : ${ips.join(", ")}${countries.length ? ` · pays : ${countries.join(", ")}` : ""}. Détail complet dans le parcours visiteur.`;
  return (
    <span
      data-testid="mobility-badge"
      title={title}
      className={`inline-flex items-center gap-1 ${compact ? "px-1.5 py-0.5 text-[9px]" : "px-2 py-0.5 text-[10px]"} uppercase tracking-[0.06em] rounded-sm border whitespace-nowrap ${multiCountry ? "bg-amber-50 text-amber-800 border-amber-300" : "bg-sky-50 text-sky-800 border-sky-200"}`}
    >
      <Smartphone className="h-3 w-3" />
      {multiIp && <span className="font-mono normal-case">{shownIps.join(" · ")}{moreIps > 0 ? ` +${moreIps}` : ""}</span>}
      {multiCountry && <span>{shownC.map((c) => `${flagEmoji(c)} ${c}`).join(" ")}{moreC > 0 ? ` +${moreC}` : ""}</span>}
      {(moreIps > 0 || moreC > 0) && <span className="normal-case text-[9px] opacity-80">détail parcours</span>}
    </span>
  );
};

// Badge réutilisable : visiteur bloqué (IP ou pays) + nombre de requêtes refusées
export const BlockedBadge = ({ v, compact = false }) => {
  if (!v?.is_blocked && !(v?.blocked_hits > 0)) return null;
  const label = v.is_blocked
    ? (v.block_type === "country" ? `Bloqué · pays ${v.block_entry || ""}`
      : v.block_type === "session" ? "Bloqué · appareil"
      : `Bloqué · IP${v.block_entry && v.block_entry !== v.ip ? ` ${v.block_entry}` : ""}`)
    : "Requêtes refusées";
  return (
    <span
      data-testid="blocked-badge"
      title={v.last_blocked_at ? `${v.blocked_hits} requête(s) refusée(s) · dernière : ${fmtTime(v.last_blocked_at)}` : undefined}
      className={`inline-flex items-center gap-1 ${compact ? "px-1.5 py-0.5 text-[9px]" : "px-2 py-0.5 text-[10px]"} uppercase tracking-[0.08em] bg-red-100 text-red-700 border border-red-200 rounded-sm whitespace-nowrap`}
    >
      <ShieldOff className="h-3 w-3" /> {label}{v.blocked_hits > 0 ? ` · ${v.blocked_hits}` : ""}
    </span>
  );
};

// A single, readable step in the visitor journey timeline
const StepRow = ({ step, isEntry, fmtTime, fmtDuration, sessionIp }) => {
  const conf = STEP_CONF[isEntry ? "arrival" : step.type] || STEP_CONF.page;
  const Icon = conf.icon;
  const highlight = ["product", "cart", "conversion", "blocked", "block_applied"].includes(step.type) || isEntry;
  const isBlockStep = step.type === "blocked" || step.type === "block_applied";
  return (
    <div className="relative" data-testid={`journey-step-${isEntry ? "arrival" : step.type}`}>
      <span className={`absolute -left-[26px] top-0 flex items-center justify-center h-6 w-6 rounded-full border-2 ${conf.dot}`}>
        <Icon className={`h-3 w-3 ${conf.ring}`} strokeWidth={2} />
      </span>
      <div className={`rounded-sm border ${isBlockStep ? "border-red-200 bg-red-50/60" : highlight ? "border-stone bg-white" : "border-transparent bg-transparent"} ${highlight ? "p-3" : "py-1.5 px-1"}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className={`text-[10px] uppercase tracking-[0.14em] ${highlight ? "text-ash" : "text-stone"}`}>
              {isEntry ? STEP_CONF.arrival.title : conf.title}
            </p>
            <p className={`${isBlockStep ? "text-sm text-red-700 font-medium" : highlight ? "text-sm text-ink font-medium" : "text-sm text-graphite"} truncate`}>
              {step.label}
              {step.type === "cart" && step.quantity ? ` ×${step.quantity}` : ""}
            </p>
            {isEntry && step.source && (
              <p className="text-xs text-copper mt-0.5">via {step.source}</p>
            )}
            {step.type === "conversion" && (
              <p className="text-sm text-green-700 font-semibold mt-0.5">{eur(step.revenue)}</p>
            )}
            {step.type === "block_applied" && (
              <p className="text-xs text-red-700/80 mt-0.5">
                {step.by ? `par ${step.by}` : "par un administrateur"}{step.reason ? ` · motif : ${step.reason}` : ""}
              </p>
            )}
            {step.type === "blocked" && step.path && (
              <p className="text-xs text-red-700/80 mt-0.5 font-mono truncate">{step.path}</p>
            )}
            {step.ip && sessionIp && step.ip !== sessionIp && (
              <p className="text-[11px] text-copper mt-0.5 font-mono" title="L'appareil a changé d'adresse IP : un blocage IP seul ne le suit pas, le blocage par appareil oui.">
                ⚠ depuis une autre IP : {step.ip}
              </p>
            )}
          </div>
          <span className={`text-[11px] whitespace-nowrap shrink-0 ${isBlockStep ? "text-red-700 font-mono font-medium" : "text-ash"}`} title={step.created_at || undefined}>
            {isBlockStep ? fmtTimeExact(step.created_at) : fmtTime(step.created_at)}
          </span>
        </div>
        {step.time_on_page_ms > 0 && (
          <p className="text-[11px] text-ash mt-1 flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtDuration(step.time_on_page_ms)} sur la page</p>
        )}
      </div>
    </div>
  );
};

export function VisitorJourney({ sessionId, onClose }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/superadmin/visitors/${sessionId}`).then(({ data }) => setD(data)).catch(() => setErr(true));
  }, [sessionId]);
  useEffect(() => { load(); }, [load]);

  const blockDevice = async () => {
    setBusy(true);
    try {
      await api.post("/superadmin/session-block", { session_id: sessionId, reason: "Blocage de l'appareil depuis le parcours visiteur" });
      toast.success("Appareil bloqué : il restera bloqué même si son IP change");
      load();
    } catch (e) {
      const st = e.response?.status;
      const detail = e.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail
          : st === 404 ? "Fonction indisponible : le backend n'est pas encore à jour (redéploiement requis)"
          : st ? `Erreur ${st} lors du blocage de l'appareil` : "Réseau ou CORS : le backend n'a pas répondu"
      );
    } finally {
      setBusy(false);
    }
  };
  const unblockDevice = async () => {
    setBusy(true);
    try {
      await api.delete(`/superadmin/session-blocks/${encodeURIComponent(sessionId)}`);
      toast.success("Appareil débloqué");
      load();
    } catch (e) {
      toast.error("Erreur lors du déblocage");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-end bg-ink/60">
      <div className="bg-sand w-full max-w-xl h-full overflow-y-auto border-l border-stone" data-testid="visitor-journey">
        <div className="flex items-center justify-between p-5 border-b border-stone bg-white sticky top-0 z-10">
          <div className="flex items-center gap-2">
            <Route className="h-5 w-5 text-gold" />
            <h2 className="font-serif text-2xl text-ink">Parcours visiteur</h2>
          </div>
          <button onClick={onClose} className="text-ash hover:text-ink"><X className="h-5 w-5" /></button>
        </div>
        {err ? (
          <div className="p-8"><p className="text-destructive">Erreur de chargement.</p></div>
        ) : !d ? (
          <div className="p-8 space-y-4">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-16 bg-white border border-stone animate-pulse" />)}</div>
        ) : (
          <div className="p-5 space-y-5">
            {(d.block?.is_blocked || d.block?.blocked_hits > 0) && (
              <div className="bg-red-50 border border-red-200 p-4 text-sm" data-testid="journey-blocked">
                <div className="flex items-center gap-2 text-red-700 font-medium">
                  <ShieldOff className="h-4 w-4" />
                  {d.block.is_blocked
                    ? (d.block.block_type === "country"
                        ? `Visiteur bloqué — pays ${d.block.block_entry}`
                        : `Visiteur bloqué — IP ${d.block.block_entry || d.session.ip}`)
                    : "Visiteur ayant subi des refus d'accès"}
                </div>
                {d.block.applied_at && (
                  <p className="text-xs text-red-700 mt-1.5">
                    Blocage appliqué le <span className="font-mono font-medium">{fmtTimeExact(d.block.applied_at)}</span>
                    {d.block.applied_by ? ` par ${d.block.applied_by}` : ""}{d.block.applied_reason ? ` · motif : ${d.block.applied_reason}` : ""}
                  </p>
                )}
                <p className="text-xs text-red-700/80 mt-1">
                  {d.block.blocked_hits > 0 ? (
                    <>
                      {d.block.blocked_hits} requête{d.block.blocked_hits > 1 ? "s" : ""} refusée{d.block.blocked_hits > 1 ? "s" : ""}
                      {" · "}première le <span className="font-mono">{fmtTimeExact(d.block.first_blocked_at)}</span>
                      {" · "}dernière le <span className="font-mono">{fmtTimeExact(d.block.last_blocked_at)}</span>
                    </>
                  ) : (
                    "Aucune requête refusée enregistrée pour le moment : le blocage a été appliqué après sa dernière visite."
                  )}
                </p>
                <p className="text-[11px] text-red-700/70 mt-1">Chaque refus apparaît dans le parcours ci-dessous, à l'heure exacte (à la seconde), à sa place entre les pages vues.</p>
              </div>
            )}

            {(d.block?.ip_changed || d.block?.is_blocked || d.session.ip) && (
              <div className={`border p-4 text-sm ${d.block?.ip_changed && !d.block?.session_blocked ? "bg-amber-50 border-amber-200" : "bg-white border-stone"}`} data-testid="journey-device">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="text-[11px] uppercase tracking-[0.14em] text-ash">Appareil (identifiant de session)</p>
                    <p className="font-mono text-xs text-graphite truncate" title={d.session.session_id}>{d.session.session_id}</p>
                    {d.block?.ip_changed ? (
                      <p className="text-xs text-amber-800 mt-1">
                        ⚠ Cet appareil a utilisé {d.block.ip_history.length} adresses IP : <span className="font-mono">{d.block.ip_history.join(", ")}</span>.
                        Un blocage IP seul ne le suit pas quand son adresse change (réseau mobile). Le blocage par appareil, lui, le suit.
                      </p>
                    ) : (
                      <p className="text-xs text-ash mt-1">Le blocage par appareil suit cet identifiant même si l'adresse IP change.</p>
                    )}
                  </div>
                  {d.block?.session_blocked ? (
                    <button onClick={unblockDevice} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-[0.1em] border border-green-300 text-green-700 bg-white hover:bg-green-50 rounded-sm disabled:opacity-50">
                      <ShieldCheck className="h-3.5 w-3.5" /> Débloquer l'appareil
                    </button>
                  ) : (
                    <button onClick={blockDevice} disabled={busy} data-testid="block-device" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-[0.1em] bg-red-600 text-white hover:bg-red-700 rounded-sm disabled:opacity-50">
                      <Ban className="h-3.5 w-3.5" /> Bloquer l'appareil
                    </button>
                  )}
                </div>
              </div>
            )}
            <div className="bg-white border border-stone p-4 space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-ash uppercase text-xs tracking-[0.15em] flex items-center gap-2">Visiteur <RiskPill risk={d.block?.risk} compact /></span>
                {d.session.converted ? (
                  <span className="px-2 py-0.5 text-[10px] uppercase bg-green-100 text-green-800 border border-green-200 rounded-sm">Converti · {eur(d.session.revenue)}</span>
                ) : (
                  <span className="px-2 py-0.5 text-[10px] uppercase bg-stone/40 text-graphite border border-stone rounded-sm">Non converti</span>
                )}
              </div>
              <p className="text-ink font-medium">{d.session.user_email || "Visiteur anonyme"} {d.session.ip && <span className="text-xs text-ash font-mono font-normal">· {d.session.ip}</span>}</p>
              {d.session.bot_type && <div><BotBadge v={d.session} /></div>}
              <div className="grid grid-cols-2 gap-2 text-graphite pt-1">
                <span className="flex items-center gap-1.5" title={d.session.location_history?.length ? "Localisation de l'IP courante (l'appareil a changé d'adresse)" : undefined}>
                  <MapPin className="h-3.5 w-3.5 text-ash" /> {flagEmoji(d.session.country_code)} {d.session.city ? `${d.session.city}, ` : ""}{d.session.country || "Localisation inconnue"}
                  {d.session.first_country_code && d.session.first_country_code !== d.session.country_code && (
                    <span className="text-[10px] text-copper" title="Pays de la première visite de cette session">(arrivé depuis {flagEmoji(d.session.first_country_code)} {d.session.first_country || d.session.first_country_code})</span>
                  )}
                </span>
                <span className="flex items-center gap-1.5"><Monitor className="h-3.5 w-3.5 text-ash" /> {d.session.device} · {d.session.os}</span>
                <span className="flex items-center gap-1.5"><Globe className="h-3.5 w-3.5 text-ash" /> {d.session.browser}</span>
                <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-ash" /> {fmtDuration(d.session.duration_ms)} · {d.session.page_count} pages</span>
              </div>
              {d.session.referrer && <p className="text-xs text-ash pt-1 truncate">Source : {d.session.referrer}</p>}
              {d.campaign && <p className="text-xs text-ash">Campagne : <span className="font-mono">{d.campaign.code}</span> — {d.campaign.name}</p>}
            </div>

            {(d.session.multi_ip || d.session.multi_country) && (
              <div className="bg-white border border-amber-200 p-4" data-testid="journey-locations">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <p className="text-xs uppercase tracking-[0.15em] text-ash flex items-center gap-1.5"><Smartphone className="h-3.5 w-3.5 text-copper" /> Adresses & localisations de l'appareil</p>
                  <MobilityBadge v={d.session} compact />
                </div>
                <p className="text-[11px] text-graphite mb-2">
                  Cet appareil a conservé son identifiant de session tout en changeant {d.session.multi_country ? "de pays" : "d'adresse IP"} :
                  {" "}{d.session.ip_count} IP{d.session.ip_count > 1 ? "s" : ""} · {d.session.country_count} pays. Itinérance mobile, changement Wi-Fi/4G, voyage ou VPN.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="text-[10px] uppercase tracking-[0.1em] text-ash border-b border-stone"><th className="text-left py-1.5 pr-3">#</th><th className="text-left py-1.5 pr-3">IP</th><th className="text-left py-1.5 pr-3">Pays</th><th className="text-left py-1.5 pr-3">Ville</th><th className="text-left py-1.5">Vu depuis</th></tr></thead>
                    <tbody className="divide-y divide-stone">
                      {d.session.locations.map((l, i) => (
                        <tr key={`${l.ip}-${i}`} className={l.ip === d.session.last_ip ? "bg-sand/50" : ""}>
                          <td className="py-1.5 pr-3 text-ash">{i + 1}</td>
                          <td className="py-1.5 pr-3 font-mono text-ink">{l.ip}{l.ip === d.session.last_ip && <span className="ml-1 text-[9px] uppercase text-copper">actuelle</span>}</td>
                          <td className="py-1.5 pr-3">{l.country_code ? `${flagEmoji(l.country_code)} ${l.country || l.country_code}` : "—"}</td>
                          <td className="py-1.5 pr-3">{l.city || "—"}</td>
                          <td className="py-1.5 text-ash">{l.at ? fmtTimeExact(l.at) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {d.related_sessions?.length > 0 && (
              <div className="bg-white border border-stone p-4" data-testid="journey-related">
                <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                  <p className="text-xs uppercase tracking-[0.15em] text-ash">Même appareil probable — {d.related_sessions.length} autre{d.related_sessions.length > 1 ? "s" : ""} session{d.related_sessions.length > 1 ? "s" : ""}</p>
                  {d.related_sessions.some((r) => !r.session_blocked) && (
                    <button
                      onClick={async () => {
                        setBusy(true);
                        try {
                          const ids = d.related_sessions.filter((r) => !r.session_blocked).map((r) => r.session_id);
                          const { data: r } = await api.post("/superadmin/session-block-many", { session_ids: ids, reason: "Même appareil probable qu'une session bloquée" });
                          toast.success(`${r.blocked} session(s) bloquée(s)`); load();
                        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
                      }}
                      disabled={busy}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-[0.1em] border border-red-200 text-red-600 bg-white hover:bg-red-50 rounded-sm disabled:opacity-50"
                    >
                      <Ban className="h-3.5 w-3.5" /> Bloquer ces sessions
                    </button>
                  )}
                </div>
                <p className="text-[11px] text-graphite mb-2">Sessions partageant la même empreinte technique (navigateur, langue, écran — hachée, jamais stockée en clair). Utile quand les cookies sont refusés et que chaque visite crée un nouvel identifiant.</p>
                <div className="divide-y divide-stone">
                  {d.related_sessions.map((r) => (
                    <div key={r.session_id} className="py-1.5 flex items-center gap-3 text-xs flex-wrap">
                      <span className="font-mono text-ash truncate max-w-[120px]" title={r.session_id}>{r.session_id.slice(0, 12)}…</span>
                      <span className="font-mono text-graphite">{r.last_ip || r.ip || "—"}</span>
                      <span className="text-graphite">{flagEmoji(r.country_code)} {r.city ? `${r.city}, ` : ""}{r.country || "—"}</span>
                      <span className="text-ash">{r.page_count || 0} pages · {fmtTime(r.last_seen)}</span>
                      {r.user_email && <span className="text-ash">{r.user_email}</span>}
                      {r.session_blocked && <span className="text-[9px] uppercase px-1.5 py-0.5 rounded-sm bg-red-100 text-red-700 border border-red-200">bloquée</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {d.session.products_viewed?.length > 0 && (
              <div className="bg-white border border-stone p-4">
                <p className="text-xs uppercase tracking-[0.15em] text-ash mb-2">Produits consultés ({d.session.products_viewed.length})</p>
                <div className="flex flex-wrap gap-2">
                  {d.session.products_viewed.map((p) => (
                    <span key={p.slug} className="text-xs bg-sand border border-stone px-2 py-1 text-graphite">{p.name}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-white border border-stone p-4">
              <div className="flex items-center gap-2 mb-1">
                <Route className="h-4 w-4 text-gold" />
                <p className="text-xs uppercase tracking-[0.15em] text-ash">Parcours</p>
              </div>
              <p className="text-xs text-ash mb-4">De l&apos;arrivée à la dernière action — étapes clés mises en avant.</p>

              {(!d.entry && d.timeline.length === 0) ? (
                <p className="text-sm text-ash">Aucune activité enregistrée.</p>
              ) : (
                <div className="relative pl-[26px] space-y-2.5 before:absolute before:left-[11px] before:top-2 before:bottom-2 before:w-px before:bg-stone">
                  {d.entry && (
                    <StepRow step={d.entry} isEntry fmtTime={fmtTime} fmtDuration={fmtDuration} sessionIp={d.session.ip} />
                  )}
                  {d.timeline.map((t, i) => (
                    <StepRow key={i} step={t} fmtTime={fmtTime} fmtDuration={fmtDuration} sessionIp={d.session.ip} />
                  ))}
                  {!d.session.converted && (
                    <div className="relative">
                      <span className="absolute -left-[26px] top-0 flex items-center justify-center h-6 w-6 rounded-full border-2 border-stone bg-white">
                        <ArrowRight className="h-3 w-3 text-ash" strokeWidth={2} />
                      </span>
                      <p className="text-sm text-ash py-1">A quitté le site — durée totale {fmtDuration(d.session.duration_ms)}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CampaignDetail({ campaignId, onClose, origin }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [tab, setTab] = useState("apercu");
  const [visitors, setVisitors] = useState(null);
  const [selectedVisitor, setSelectedVisitor] = useState(null);

  useEffect(() => {
    api.get(`/superadmin/campaigns/${campaignId}`).then(({ data }) => setD(data)).catch(() => setErr(true));
  }, [campaignId]);

  useEffect(() => {
    if (tab === "visiteurs" && visitors === null) {
      api.get(`/superadmin/campaigns/${campaignId}/visitors`).then(({ data }) => setVisitors(data.visitors)).catch(() => setVisitors([]));
    }
  }, [tab, campaignId, visitors]);

  const trackingUrl = d ? `${origin}${d.landing_path || "/"}${(d.landing_path || "/").includes("?") ? "&" : "?"}ref=${d.code}` : "";
  const copyLink = () => { navigator.clipboard.writeText(trackingUrl); toast.success("Lien tracké copié."); };

  const maxGeo = d?.geo_breakdown?.[0]?.count || 1;
  const maxDev = Math.max(1, ...(d?.device_breakdown || []).map((x) => x.count));
  const maxBro = Math.max(1, ...(d?.browser_breakdown || []).map((x) => x.count));
  const maxProd = d?.top_products_viewed?.[0]?.views || 1;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-end bg-ink/50">
      <div className="bg-sand w-full max-w-3xl h-full overflow-y-auto border-l border-stone" data-testid="campaign-detail">
        {err ? (
          <div className="p-8"><p className="text-destructive">Erreur de chargement.</p></div>
        ) : !d ? (
          <div className="p-8 space-y-4">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-24 bg-white border border-stone animate-pulse" />)}</div>
        ) : (
          <>
            <div className="border-b border-stone bg-white sticky top-0 z-10">
              <div className="flex items-center justify-between p-5">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-ash">{CHANNEL_LABELS[d.channel] || d.channel}</p>
                  <h2 className="font-serif text-2xl text-ink">{d.name}</h2>
                </div>
                <button onClick={onClose} className="text-ash hover:text-ink"><X className="h-5 w-5" /></button>
              </div>
              <div className="flex gap-1 px-5">
                {[["apercu", "Aperçu"], ["visiteurs", "Parcours visiteurs"]].map(([k, label]) => (
                  <button
                    key={k}
                    data-testid={`detail-tab-${k}`}
                    onClick={() => setTab(k)}
                    className={`px-4 py-2.5 text-xs uppercase tracking-[0.12em] border-b-2 transition-colors ${tab === k ? "border-gold text-ink" : "border-transparent text-ash hover:text-graphite"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {tab === "apercu" && (
            <div className="p-5 space-y-5">
              <div className="bg-white border border-stone p-4">
                <p className="text-xs uppercase tracking-[0.15em] text-ash mb-2">Lien tracké</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs text-graphite break-all bg-sand px-3 py-2 border border-stone">{trackingUrl}</code>
                  <button onClick={copyLink} className="bg-ink text-cream px-3 py-2 hover:bg-gold hover:text-ink transition-colors" title="Copier"><Copy className="h-4 w-4" /></button>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <Stat icon={MousePointerClick} label="Clics" value={d.stats.clicks} />
                <Stat icon={Target} label="Conversions" value={d.stats.conversions} />
                <Stat icon={Euro} label="CA généré" value={fmtMoney(d.stats.revenue)} />
                <Stat icon={Percent} label="Taux conv." value={fmtPct(d.stats.conversion_rate)} />
                <Stat icon={TrendingUp} label="ROI" value={fmtPct(d.stats.roi)} />
                <Stat icon={Wallet} label="Commission" value={fmtMoney(d.stats.commission)} />
                <Stat icon={Users} label="Visiteurs" value={d.visitor_count} />
                <Stat icon={Clock} label="Durée moyenne" value={fmtDuration(d.avg_duration_ms)} />
                <Stat icon={ArrowRight} label="Taux de rebond" value={fmtPct(d.bounce_rate)} />
              </div>

              <Card title="Performance (30 jours)">
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={d.series}>
                    <defs>
                      <linearGradient id="cR" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={GOLD} stopOpacity={0.4} />
                        <stop offset="95%" stopColor={GOLD} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E7E2D8" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Area type="monotone" dataKey="clicks" stroke={COPPER} strokeWidth={1.5} fillOpacity={0} name="Clics" />
                    <Area type="monotone" dataKey="conversions" stroke={INK} strokeWidth={1.5} fillOpacity={0} name="Conversions" />
                    <Area type="monotone" dataKey="revenue" stroke={GOLD} strokeWidth={2} fill="url(#cR)" name="CA (€)" />
                  </AreaChart>
                </ResponsiveContainer>
              </Card>

              <div className="grid sm:grid-cols-2 gap-4">
                <Card title="Localisation">
                  {d.geo_breakdown.length === 0 ? <p className="text-sm text-ash">Aucune donnée.</p> : (
                    <div className="space-y-2">
                      {d.geo_breakdown.map((g) => <MiniBar key={g.location} label={g.location} count={g.count} total={maxGeo} icon={MapPin} />)}
                    </div>
                  )}
                </Card>
                <Card title="Appareils & navigateurs">
                  {d.device_breakdown.length === 0 ? <p className="text-sm text-ash">Aucune donnée.</p> : (
                    <div className="space-y-2">
                      {d.device_breakdown.map((g) => <MiniBar key={g.name} label={g.name} count={g.count} total={maxDev} icon={Monitor} />)}
                      <div className="pt-1 border-t border-stone mt-2" />
                      {d.browser_breakdown.map((g) => <MiniBar key={g.name} label={g.name} count={g.count} total={maxBro} icon={Globe} />)}
                    </div>
                  )}
                </Card>
              </div>

              <Card title="Produits qui ont attiré">
                {d.top_products_viewed.length === 0 ? <p className="text-sm text-ash">Aucun produit consulté pour le moment.</p> : (
                  <div className="space-y-2">
                    {d.top_products_viewed.map((p) => <MiniBar key={p.slug} label={p.name} count={p.views} total={maxProd} icon={Eye} />)}
                  </div>
                )}
              </Card>

              <Card title="Conversions récentes">
                {d.recent_conversions.length === 0 ? (
                  <p className="text-sm text-ash">Aucune conversion attribuée pour le moment.</p>
                ) : (
                  <div className="divide-y divide-stone">
                    {d.recent_conversions.map((o) => (
                      <div key={o.id} className="flex items-center justify-between py-2.5 text-sm">
                        <div>
                          <p className="text-ink">{o.user_email || "Invité"}</p>
                          <p className="text-xs text-ash">{(o.created_at || "").slice(0, 10)} · {o.customer_type === "new" ? "Nouveau client" : "Client récurrent"}</p>
                        </div>
                        <p className="font-medium text-ink">{eur(o.total)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
            )}

            {tab === "visiteurs" && (
            <div className="p-5">
              {visitors === null ? (
                <div className="space-y-3">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-12 bg-white border border-stone animate-pulse" />)}</div>
              ) : visitors.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="h-10 w-10 text-stone mx-auto mb-3" strokeWidth={1.2} />
                  <p className="text-graphite">Aucun visiteur tracké pour cette campagne.</p>
                  <p className="text-xs text-ash mt-1">Les visiteurs arrivés via le lien tracké apparaîtront ici.</p>
                </div>
              ) : (
                <div className="bg-white border border-stone">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[820px] text-sm" data-testid="visitors-table">
                      <thead>
                        <tr className="text-left text-xs uppercase tracking-[0.1em] text-ash border-b border-stone">
                          <th className="py-3 px-3">Localisation</th>
                          <th className="py-3 px-2">Appareil</th>
                          <th className="py-3 px-2 text-right">Durée</th>
                          <th className="py-3 px-2 text-right">Pages</th>
                          <th className="py-3 px-2 text-right">Produits</th>
                          <th className="py-3 px-2 text-right">Panier</th>
                          <th className="py-3 px-2">Statut</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-stone">
                        {visitors.map((v) => (
                          <tr key={v.session_id} onClick={() => setSelectedVisitor(v.session_id)} className="hover:bg-sand/60 cursor-pointer" data-testid="visitor-row">
                            <td className="py-3 px-3 text-graphite">
                              {flagEmoji(v.country_code)} {v.city ? `${v.city}, ` : ""}{v.country || "Inconnu"}
                              <BlockedBadge v={v} compact /> <BotBadge v={v} compact /> <MobilityBadge v={v} compact />
                            </td>
                            <td className="py-3 px-2 text-graphite">{v.device} · {v.browser}</td>
                            <td className="py-3 px-2 text-right text-graphite">{fmtDuration(v.duration_ms)}</td>
                            <td className="py-3 px-2 text-right text-graphite">{v.page_count}</td>
                            <td className="py-3 px-2 text-right text-graphite">{v.products_count}</td>
                            <td className="py-3 px-2 text-right text-graphite">{v.cart_adds_count}</td>
                            <td className="py-3 px-2">
                              {v.converted ? (
                                <span className="text-[10px] uppercase bg-green-100 text-green-800 border border-green-200 px-2 py-0.5 rounded-sm">{eur(v.revenue)}</span>
                              ) : (
                                <span className="text-[10px] uppercase text-ash">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            )}
          </>
        )}
      </div>
      {selectedVisitor && <VisitorJourney sessionId={selectedVisitor} onClose={() => setSelectedVisitor(null)} />}
    </div>
  );
}

function OrganicVisitorsPreview() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    api.get("/superadmin/regie/organic-visitors", { params: { days: 30, limit: 5 } })
      .then(({ data }) => setData(data))
      .catch(() => setErr(true));
  }, []);
  const list = data?.visitors || [];
  const agg = data?.aggregates || { total_revenue: 0, top_countries: [] };
  return (
    <div className="mt-6" data-testid="organic-visitors-preview">
      <Card
        title="Visiteurs organiques"
        action={
          <Link
            to="/admin/regie/visiteurs-organiques"
            data-testid="link-organic-visitors-page"
            className="flex items-center gap-2 text-xs uppercase tracking-[0.1em] bg-ink text-cream px-3 py-1.5 hover:bg-gold hover:text-ink transition-colors"
          >
            Voir tout <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        }
      >
        {err ? (
          <p className="text-destructive">Erreur de chargement.</p>
        ) : !data ? (
          <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-10 bg-sand animate-pulse" />)}</div>
        ) : (
          <>
            <div className="grid sm:grid-cols-4 gap-3 mb-4">
              <div className="bg-sand rounded-sm border border-stone p-3">
                <p className="text-[10px] uppercase tracking-[0.14em] text-ash flex items-center gap-1"><Users className="h-3 w-3" /> Total 30j</p>
                <p className="font-serif text-2xl text-ink mt-1">{data.total}</p>
              </div>
              <div className="bg-sand rounded-sm border border-stone p-3">
                <p className="text-[10px] uppercase tracking-[0.14em] text-ash flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Convertis</p>
                <p className="font-serif text-2xl text-ink mt-1">{data.converted}</p>
                <p className="text-[11px] text-ash mt-0.5">CA {eur(agg.total_revenue || 0)}</p>
              </div>
              <div className="bg-sand rounded-sm border border-stone p-3">
                <p className="text-[10px] uppercase tracking-[0.14em] text-ash flex items-center gap-1"><Eye className="h-3 w-3" /> Pages vues</p>
                <p className="font-serif text-2xl text-ink mt-1">{agg.total_pages || 0}</p>
              </div>
              <div className="bg-sand rounded-sm border border-stone p-3">
                <p className="text-[10px] uppercase tracking-[0.14em] text-ash flex items-center gap-1"><Clock className="h-3 w-3" /> Durée moy.</p>
                <p className="font-serif text-2xl text-ink mt-1">{fmtDuration(agg.avg_duration_ms || 0)}</p>
              </div>
            </div>
            {list.length === 0 ? (
              <p className="text-sm text-ash text-center py-4">Aucun visiteur organique sur les 30 derniers jours.</p>
            ) : (
              <>
                <p className="text-[11px] uppercase tracking-[0.14em] text-ash mb-2">5 derniers visiteurs</p>
                <div className="divide-y divide-stone">
                  {list.map((v) => (
                    <div key={v.session_id} className="py-2.5 flex items-center gap-3 text-xs">
                      <span className="text-graphite font-mono w-24 truncate">{v.ip || "—"}</span>
                      <span className="text-graphite flex-1 truncate">{flagEmoji(v.country_code)} {v.city ? `${v.city}, ` : ""}{v.country || "—"} <BlockedBadge v={v} compact /> <MobilityBadge v={v} compact /></span>
                      <span className="text-ash">{v.page_count} pages · {fmtDuration(v.duration_ms)}</span>
                      <span className="text-ash whitespace-nowrap">{fmtTime(v.last_seen)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </Card>
    </div>
  );
}



function BotsPanel() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [busyIp, setBusyIp] = useState(null);

  const load = useCallback(() => {
    api.get("/superadmin/regie/bots", { params: { days: 30 } })
      .then(({ data }) => { setData(data); setErr(false); })
      .catch(() => setErr(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  const refreshRanges = async () => {
    setRefreshing(true);
    try {
      const { data: r } = await api.post("/superadmin/regie/bots/refresh");
      if (r.ok) toast.success(`Plages IP officielles rechargées : ${Object.entries(r.ranges).map(([k, v]) => `${k} ${v}`).join(", ")}`);
      else toast.warning("Téléchargement des plages impossible — vérification par DNS inversé uniquement");
      load();
    } catch (e) {
      toast.error("Erreur lors du rechargement des plages");
    } finally {
      setRefreshing(false);
    }
  };

  const blockIp = async (row) => {
    setBusyIp(row.ip);
    try {
      await api.post("/superadmin/ip-block", { ip: row.ip, reason: `Robot ${row.bot_type === "impostor" ? "imposteur" : ""} ${row.bot_name || ""}`.trim() });
      toast.success(`IP ${row.ip} bloquée`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors du blocage");
    } finally {
      setBusyIp(null);
    }
  };

  const ranges = data?.ranges || {};
  const anyLoaded = Object.values(ranges).some((r) => r.loaded);
  const counts = data?.counts || { verified: 0, impostor: 0, bot: 0 };

  return (
    <div className="mt-6" data-testid="bots-panel">
      <Card
        title="Robots & vérification Google / Bing"
        action={
          <button
            onClick={refreshRanges}
            disabled={refreshing}
            data-testid="bots-refresh-ranges"
            className="flex items-center gap-2 text-xs uppercase tracking-[0.1em] border border-stone bg-white px-3 py-1.5 text-graphite hover:bg-sand transition-colors disabled:opacity-50"
            title="Retélécharger les plages IP officielles publiées par Google et Bing"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} /> Recharger les plages
          </button>
        }
      >
        {err ? (
          <p className="text-destructive">Erreur de chargement.</p>
        ) : !data ? (
          <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-10 bg-sand animate-pulse" />)}</div>
        ) : (
          <>
            <p className="text-xs text-graphite mb-4">
              Un robot qui se dit Googlebot ou Bingbot n'est reconnu que si son adresse IP figure dans les plages officielles publiées par le moteur
              (secours : DNS inversé puis direct). Les robots vérifiés ne sont jamais bloqués, ni par IP ni par pays, pour ne pas couper l'indexation.
              Un « faux robot » usurpe Google ou Bing depuis une IP étrangère au moteur : c'est un robot malveillant probable, à bloquer.
            </p>

            {/* Statut des plages officielles */}
            <div className="grid sm:grid-cols-2 gap-3 mb-4" data-testid="bots-ranges">
              {Object.entries(ranges).map(([name, r]) => (
                <div key={name} className={`rounded-sm border p-3 ${r.loaded ? "bg-emerald-50/60 border-emerald-200" : "bg-red-50/60 border-red-200"}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-ink flex items-center gap-1.5"><ShieldCheck className={`h-4 w-4 ${r.loaded ? "text-emerald-600" : "text-red-500"}`} /> {name}</p>
                    <span className={`text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 rounded-sm border ${r.loaded ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-red-100 text-red-700 border-red-200"}`}>
                      {r.loaded ? "Plages chargées" : "Non chargées"}
                    </span>
                  </div>
                  <p className="text-xs text-graphite mt-1.5">
                    {r.loaded ? `${r.prefixes} plages IP officielles · mises à jour ${r.fetched_at ? fmtTime(r.fetched_at) : "—"}` : "Téléchargement impossible pour l'instant — vérification par DNS inversé uniquement"}
                  </p>
                  <p className="text-[10px] text-ash mt-1 truncate" title={r.source}>Source : {r.source}</p>
                </div>
              ))}
              {!anyLoaded && (
                <p className="sm:col-span-2 text-xs text-red-700">Aucune plage officielle chargée : cliquez sur « Recharger les plages ». Sans elles, seule la vérification DNS inversé distingue les vrais robots.</p>
              )}
            </div>

            {/* Compteurs 30 jours */}
            <div className="grid grid-cols-3 gap-3 mb-4" data-testid="bots-counts">
              <div className="bg-sand rounded-sm border border-stone p-3">
                <p className="text-[10px] uppercase tracking-[0.14em] text-ash">Robots vérifiés (30 j)</p>
                <p className="font-serif text-2xl text-emerald-700 mt-1">{counts.verified}</p>
                <p className="text-[11px] text-ash">{data.views?.verified || 0} pages</p>
              </div>
              <div className="bg-sand rounded-sm border border-stone p-3">
                <p className="text-[10px] uppercase tracking-[0.14em] text-ash">Faux robots (30 j)</p>
                <p className="font-serif text-2xl text-red-600 mt-1">{counts.impostor}</p>
                <p className="text-[11px] text-ash">usurpent Google / Bing</p>
              </div>
              <div className="bg-sand rounded-sm border border-stone p-3">
                <p className="text-[10px] uppercase tracking-[0.14em] text-ash">Autres robots (30 j)</p>
                <p className="font-serif text-2xl text-ink mt-1">{counts.bot}</p>
                <p className="text-[11px] text-ash">crawlers, outils, headless</p>
              </div>
            </div>

            {/* Robots récents */}
            {data.recent.length === 0 ? (
              <p className="text-sm text-ash text-center py-3">Aucun robot détecté sur les 30 derniers jours (la classification s'applique aux visites postérieures au déploiement).</p>
            ) : (
              <>
                <p className="text-[11px] uppercase tracking-[0.14em] text-ash mb-2">Robots récents</p>
                <div className="divide-y divide-stone">
                  {data.recent.map((r) => (
                    <div key={r.session_id} className="py-2.5 flex items-center gap-3 text-xs flex-wrap" data-testid="bot-row">
                      <span className="text-graphite font-mono w-32 truncate">{r.ip || "—"}</span>
                      <BotBadge v={r} compact />
                      <BlockedBadge v={r} compact />
                      <span className="text-graphite flex-1 truncate min-w-[120px]">{flagEmoji(r.country_code)} {r.city ? `${r.city}, ` : ""}{r.country || "—"}</span>
                      <span className="text-ash">{r.page_count} pages</span>
                      <span className="text-ash whitespace-nowrap">{fmtTime(r.last_seen)}</span>
                      {r.bot_type !== "verified" && !r.is_blocked && r.ip && (
                        <button
                          onClick={() => blockIp(r)}
                          disabled={busyIp === r.ip}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase border border-red-200 text-red-600 bg-white hover:bg-red-50 rounded-sm disabled:opacity-50"
                          title="Bloquer cette IP"
                        >
                          <Ban className="h-3 w-3" /> Bloquer
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

export default function SuperRegie() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [campaigns, setCampaigns] = useState(null);
  const [err, setErr] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [campaignTab, setCampaignTab] = useState("actives");
  const [siteConfig, setSiteConfig] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const load = useCallback(() => {
    Promise.all([
      api.get("/superadmin/regie/overview"),
      api.get("/superadmin/campaigns"),
      api.get("/site-config"),
    ])
      .then(([ov, cp, sc]) => { setOverview(ov.data); setCampaigns(cp.data); setSiteConfig(sc.data); })
      .catch(() => setErr(true));
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleCookieBanner = async (enabled) => {
    setSavingConfig(true);
    try {
      const { data } = await api.put("/superadmin/site-config", { cookie_consent_enabled: enabled });
      setSiteConfig(data);
      toast.success(enabled ? "Bandeau cookies activé." : "Bandeau cookies désactivé.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingConfig(false);
    }
  };

  const onSaved = () => { setShowForm(false); setEditing(null); load(); };

  const changeStatus = async (c, status) => {
    try {
      await api.patch(`/superadmin/campaigns/${c.id}/status`, { status });
      toast.success(`Campagne ${STATUS_LABELS[status].toLowerCase()}.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const terminate = (c) => {
    if (!window.confirm(`Terminer la campagne "${c.name}" ?\n\nElle sera déplacée dans les campagnes archivées. Vous pourrez la reprendre à tout moment.`)) return;
    changeStatus(c, "ended");
  };

  const reactivate = (c) => {
    if (!window.confirm(`Reprendre la campagne "${c.name}" ?\n\nElle redeviendra active et recommencera à tracker les visiteurs.`)) return;
    changeStatus(c, "active");
  };

  const remove = async (c) => {
    if (!window.confirm(`Supprimer la campagne "${c.name}" ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/superadmin/campaigns/${c.id}`);
      toast.success("Campagne supprimée.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const copyLink = (c) => {
    const lp = c.landing_path || "/";
    const url = `${origin}${lp}${lp.includes("?") ? "&" : "?"}ref=${c.code}`;
    navigator.clipboard.writeText(url);
    toast.success("Lien tracké copié.");
  };

  if (user?.role !== "superadmin") return <p className="text-graphite">Accès réservé au super administrateur.</p>;
  if (err) return <p className="text-destructive">Erreur de chargement de la régie publicitaire.</p>;

  const attrData = overview
    ? [
        { name: "Payant (campagnes)", value: overview.paid_orders, fill: GOLD },
        { name: "Organique", value: overview.organic_orders, fill: COPPER },
      ]
    : [];
  const custData = overview
    ? [
        { name: "Nouveaux", value: overview.new_customers, fill: SAGE },
        { name: "Récurrents", value: overview.returning_customers, fill: INK },
      ]
    : [];

  const activeList = campaigns ? campaigns.filter((c) => c.status !== "ended") : [];
  const archivedList = campaigns ? campaigns.filter((c) => c.status === "ended") : [];
  const shownCampaigns = campaignTab === "archivees" ? archivedList : activeList;

  return (
    <div data-testid="super-regie">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-ash mb-2 flex items-center gap-2">
            <Megaphone className="h-4 w-4 text-gold" /> Super Admin
          </p>
          <h1 className="font-serif text-4xl md:text-5xl text-ink tracking-tight">Régie Publicitaire</h1>
        </div>
        <button
          data-testid="new-campaign-btn"
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="flex items-center gap-2 bg-ink text-cream px-5 py-3 text-xs uppercase tracking-[0.15em] hover:bg-gold hover:text-ink transition-colors"
        >
          <Plus className="h-4 w-4" /> Nouvelle campagne
        </button>
      </div>

      <div className="bg-white border border-stone rounded-sm p-5 mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <ShieldCheck className="h-5 w-5 text-copper shrink-0 mt-0.5" strokeWidth={1.5} />
          <div>
            <h2 className="font-serif text-xl text-ink">RGPD — Bandeau de consentement cookies</h2>
            <p className="text-sm text-graphite mt-1 max-w-xl">
              Si désactivé, aucun bandeau n&apos;est affiché et tous les visiteurs sont suivis
              normalement (comme avant). Si activé, un visiteur qui refuse reste tout de même
              suivi ici (mesure, conversions, attribution) — la seule différence est qu&apos;aucun
              identifiant n&apos;est mémorisé dans son navigateur, donc on ne peut pas savoir s&apos;il
              s&apos;agit d&apos;un nouveau visiteur ou d&apos;un visiteur déjà venu.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0" data-testid="cookie-banner-toggle-wrap">
          <span className="text-xs uppercase tracking-[0.15em] text-ash">
            {siteConfig?.cookie_consent_enabled ? "Activé" : "Désactivé"}
          </span>
          <Switch
            data-testid="cookie-banner-toggle"
            checked={!!siteConfig?.cookie_consent_enabled}
            disabled={!siteConfig || savingConfig}
            onCheckedChange={toggleCookieBanner}
            className="data-[state=checked]:bg-gold"
          />
        </div>
      </div>

      {!overview ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">{Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-28 bg-white border border-stone animate-pulse rounded-sm" />)}</div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat testid="ov-revenue" icon={Euro} label="CA attribué" value={fmtMoney(overview.total_revenue)} sub={`${overview.total_conversions} conversions`} />
            <Stat testid="ov-roi" icon={TrendingUp} label="ROI global" value={fmtPct(overview.global_roi)} sub={`Net ${fmtMoney(overview.net)}`} />
            <Stat testid="ov-cac" icon={Target} label="CAC moyen" value={fmtMoney(overview.global_cac)} sub="Coût d'acquisition" />
            <Stat testid="ov-conv" icon={Percent} label="Taux conversion" value={fmtPct(overview.global_conversion_rate)} sub={`${overview.total_clicks} clics`} />
            <Stat testid="ov-spend" icon={Wallet} label="Dépense pub" value={fmtMoney(overview.total_spend)} />
            <Stat testid="ov-commission" icon={Wallet} label="Commissions dues" value={fmtMoney(overview.total_commission)} />
            <Stat testid="ov-campaigns" icon={Megaphone} label="Campagnes" value={overview.n_campaigns} sub={`${overview.active_campaigns} actives`} />
            <Stat testid="ov-new" icon={Users} label="Nouveaux clients" value={overview.new_customers} sub={`${overview.returning_customers} récurrents`} />
          </div>

          <div className="grid lg:grid-cols-3 gap-4 mt-6">
            <div className="lg:col-span-2">
              <Card title="CA — Payant vs Organique (30 jours)">
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={overview.revenue_series}>
                    <defs>
                      <linearGradient id="gPaid" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={GOLD} stopOpacity={0.5} />
                        <stop offset="95%" stopColor={GOLD} stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gOrg" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COPPER} stopOpacity={0.4} />
                        <stop offset="95%" stopColor={COPPER} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E7E2D8" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v) => eur(v)} />
                    <Legend />
                    <Area type="monotone" dataKey="paid" stroke={GOLD} strokeWidth={2} fill="url(#gPaid)" name="Payant" />
                    <Area type="monotone" dataKey="organic" stroke={COPPER} strokeWidth={2} fill="url(#gOrg)" name="Organique" />
                  </AreaChart>
                </ResponsiveContainer>
              </Card>
            </div>
            <div className="space-y-4">
              <Card title="Attribution des ventes">
                <ResponsiveContainer width="100%" height={110}>
                  <PieChart>
                    <Pie data={attrData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={28} outerRadius={48} paddingAngle={2}>
                      {attrData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-4 text-xs text-graphite">
                  <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: GOLD }} /> Payant {overview.paid_orders}</span>
                  <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: COPPER }} /> Organique {overview.organic_orders}</span>
                </div>
              </Card>
              <Card title="Type de clients">
                <ResponsiveContainer width="100%" height={110}>
                  <PieChart>
                    <Pie data={custData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={28} outerRadius={48} paddingAngle={2}>
                      {custData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-4 text-xs text-graphite">
                  <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: SAGE }} /> Nouveaux {overview.new_customers}</span>
                  <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: INK }} /> Récurrents {overview.returning_customers}</span>
                </div>
              </Card>
            </div>
          </div>
        </>
      )}

      {/* Campaigns table */}
      <div className="mt-6">
        <Card
          title="Campagnes"
          action={
            <div className="flex gap-1" data-testid="campaign-tabs">
              <button
                data-testid="campaign-tab-actives"
                onClick={() => setCampaignTab("actives")}
                className={`px-3 py-1.5 text-xs uppercase tracking-[0.1em] border rounded-sm transition-colors ${campaignTab === "actives" ? "bg-ink text-cream border-ink" : "border-stone text-graphite hover:bg-sand"}`}
              >
                Actives ({activeList.length})
              </button>
              <button
                data-testid="campaign-tab-archivees"
                onClick={() => setCampaignTab("archivees")}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-[0.1em] border rounded-sm transition-colors ${campaignTab === "archivees" ? "bg-ink text-cream border-ink" : "border-stone text-graphite hover:bg-sand"}`}
              >
                <Archive className="h-3.5 w-3.5" /> Archivées ({archivedList.length})
              </button>
            </div>
          }
        >
          {!campaigns ? (
            <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-14 bg-sand animate-pulse" />)}</div>
          ) : shownCampaigns.length === 0 ? (
            <div className="text-center py-12">
              {campaignTab === "archivees" ? (
                <>
                  <Archive className="h-10 w-10 text-stone mx-auto mb-3" strokeWidth={1.2} />
                  <p className="text-graphite">Aucune campagne archivée.</p>
                  <p className="text-xs text-ash mt-1">Les campagnes terminées apparaîtront ici et pourront être reprises.</p>
                </>
              ) : (
                <>
                  <BarChart3 className="h-10 w-10 text-stone mx-auto mb-3" strokeWidth={1.2} />
                  <p className="text-graphite">Aucune campagne active. Créez votre première campagne publicitaire.</p>
                </>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="campaigns-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-[0.12em] text-ash border-b border-stone">
                    <th className="py-3 pr-4">Campagne</th>
                    <th className="py-3 px-2">Canal</th>
                    <th className="py-3 px-2">Statut</th>
                    <th className="py-3 px-2 text-right">Clics</th>
                    <th className="py-3 px-2 text-right">Conv.</th>
                    <th className="py-3 px-2 text-right">CA</th>
                    <th className="py-3 px-2 text-right">ROI</th>
                    <th className="py-3 px-2 text-right">Commission</th>
                    <th className="py-3 pl-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone">
                  {shownCampaigns.map((c) => (
                    <tr key={c.id} className="hover:bg-sand/60" data-testid={`campaign-row-${c.code}`}>
                      <td className="py-3 pr-4">
                        <button onClick={() => setDetailId(c.id)} className="text-left">
                          <p className="text-ink font-medium hover:text-gold transition-colors">{c.name}</p>
                          <p className="text-xs text-ash font-mono">{c.code}</p>
                        </button>
                      </td>
                      <td className="py-3 px-2 text-graphite">{CHANNEL_LABELS[c.channel] || c.channel}</td>
                      <td className="py-3 px-2">
                        <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wide border rounded-sm ${STATUS_STYLES[c.status] || ""}`}>
                          {STATUS_LABELS[c.status] || c.status}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-right text-graphite">{c.stats.clicks}</td>
                      <td className="py-3 px-2 text-right text-graphite">{c.stats.conversions}</td>
                      <td className="py-3 px-2 text-right text-ink font-medium">{eur(c.stats.revenue)}</td>
                      <td className="py-3 px-2 text-right text-graphite">{fmtPct(c.stats.roi)}</td>
                      <td className="py-3 px-2 text-right text-graphite">{eur(c.stats.commission)}</td>
                      <td className="py-3 pl-2">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => copyLink(c)} title="Copier le lien tracké" className="p-1.5 text-ash hover:text-gold"><Copy className="h-4 w-4" /></button>
                          {c.status === "ended" ? (
                            <button data-testid={`reactivate-${c.code}`} onClick={() => reactivate(c)} title="Reprendre la campagne" className="flex items-center gap-1 px-2 py-1 text-xs uppercase tracking-[0.1em] border border-green-200 text-green-700 hover:bg-green-50 rounded-sm transition-colors">
                              <RotateCcw className="h-3.5 w-3.5" /> Reprendre
                            </button>
                          ) : (
                            <>
                              {c.status === "paused" && (
                                <button data-testid={`activate-${c.code}`} onClick={() => changeStatus(c, "active")} title="Activer" className="p-1.5 text-ash hover:text-green-600"><Play className="h-4 w-4" /></button>
                              )}
                              {c.status === "active" && (
                                <button data-testid={`pause-${c.code}`} onClick={() => changeStatus(c, "paused")} title="Mettre en pause" className="p-1.5 text-ash hover:text-amber-600"><Pause className="h-4 w-4" /></button>
                              )}
                              <button data-testid={`terminate-${c.code}`} onClick={() => terminate(c)} title="Terminer" className="p-1.5 text-ash hover:text-graphite"><Square className="h-4 w-4" /></button>
                            </>
                          )}
                          <button onClick={() => { setEditing(c); setShowForm(true); }} title="Modifier" className="p-1.5 text-ash hover:text-ink"><Pencil className="h-4 w-4" /></button>
                          <button onClick={() => remove(c)} title="Supprimer" className="p-1.5 text-ash hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <OrganicVisitorsPreview />
      <BotsPanel />

      {showForm && (
        <CampaignForm initial={editing} onClose={() => { setShowForm(false); setEditing(null); }} onSaved={onSaved} />
      )}
      {detailId && (
        <CampaignDetail campaignId={detailId} origin={origin} onClose={() => setDetailId(null)} />
      )}
    </div>
  );
}
